import { DEFAULT_FILTERS } from "./filters";
import type {
  ComparisonFilters,
  HistoryWindow,
  PriceFilterMode,
  SortDirection,
} from "./types";

const NULLABLE_NUMBER_KEYS = [
  "minBuyPrice",
  "maxBuyPrice",
  "minBuyOrders",
  "maxBuyOrders",
  "minSellPrice",
  "maxSellPrice",
  "minSellOrders",
  "maxSellOrders",
  "minProfitPct",
  "maxProfitPct",
  "minDailySales",
  "minMedianDailySales",
  "minActiveDaysPct",
  "maxExpectedSaleDays",
  "minSafeVolumeProfit",
  "minMedianPrice",
  "maxMedianPrice",
  "minAveragePrice",
  "maxAveragePrice",
] as const satisfies ReadonlyArray<keyof ComparisonFilters>;

const BOOLEAN_KEYS = [
  "medianWeighted",
  "hideStickers",
  "hideNegativeDeals",
] as const satisfies ReadonlyArray<keyof ComparisonFilters>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringArray(
  value: unknown,
  fallback: readonly string[],
): string[] {
  if (!Array.isArray(value)) {
    return [...fallback];
  }
  const unique = [
    ...new Set(value.filter((item): item is string => typeof item === "string")),
  ];
  return unique.length > 0 ? unique : [...fallback];
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fallback;
}

function nullableNumber(
  value: unknown,
  fallback: number | null,
): number | null {
  if (value === null) {
    return null;
  }
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fallback;
}

export function normalizeComparisonPreset(
  value: unknown,
): ComparisonFilters {
  if (!isRecord(value)) {
    return {
      ...DEFAULT_FILTERS,
      buyMarkets: [...DEFAULT_FILTERS.buyMarkets],
      sellMarkets: [...DEFAULT_FILTERS.sellMarkets],
    };
  }

  const normalized: ComparisonFilters = {
    ...DEFAULT_FILTERS,
    buyMarkets: stringArray(value.buyMarkets, DEFAULT_FILTERS.buyMarkets),
    sellMarkets: stringArray(value.sellMarkets, DEFAULT_FILTERS.sellMarkets),
  };

  for (const key of NULLABLE_NUMBER_KEYS) {
    normalized[key] = nullableNumber(value[key], DEFAULT_FILTERS[key]);
  }
  for (const key of BOOLEAN_KEYS) {
    normalized[key] =
      typeof value[key] === "boolean" ? value[key] : DEFAULT_FILTERS[key];
  }

  const priceFilterMode = value.priceFilterMode;
  if (
    priceFilterMode === "current" ||
    priceFilterMode === "median" ||
    priceFilterMode === "average"
  ) {
    normalized.priceFilterMode = priceFilterMode as PriceFilterMode;
  }

  const medianWindowDays = value.medianWindowDays;
  if (
    medianWindowDays === 14 ||
    medianWindowDays === 30 ||
    medianWindowDays === 60
  ) {
    normalized.medianWindowDays = medianWindowDays as HistoryWindow;
  }

  const profitSort = value.profitSort;
  if (profitSort === "asc" || profitSort === "desc") {
    normalized.profitSort = profitSort as SortDirection;
  }

  normalized.profitableSpreadPct = finiteNumber(
    value.profitableSpreadPct,
    DEFAULT_FILTERS.profitableSpreadPct,
  );

  return normalized;
}

export function serializeComparisonPreset(
  filters: ComparisonFilters,
): Record<string, unknown> {
  return {
    ...normalizeComparisonPreset(filters),
    buyMarkets: [...filters.buyMarkets],
    sellMarkets: [...filters.sellMarkets],
  };
}

export function comparisonPresetsEqual(
  left: ComparisonFilters,
  right: ComparisonFilters,
): boolean {
  return JSON.stringify(serializeComparisonPreset(left)) ===
    JSON.stringify(serializeComparisonPreset(right));
}
